// src/pages/AuthorBookDetailsPage.jsx
// Independent Author Book Details Page - mirrors EditTitlePage for managed authors
// Displays Reader Credits instead of Engagement Units
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import CreatorNav from '../components/CreatorNav';
import CreatorTopMenuBar from '../components/CreatorTopMenuBar';
import CreatorMobileBottomNav from '../components/CreatorMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import TTSDecisionModal from '../components/TTSDecisionModal';
// P0 DIRECTIVE: TTSProgressModal is DEPRECATED and FORBIDDEN
// TTS is background-only, progress shown via polling surfaces
import OutlineEditorModal from '../components/outline/OutlineEditorModal';
import PostIngestionOutlineEditor from '../components/PostIngestionOutlineEditor';
import SampleSelectionModal from '../components/SampleSelectionModal';
import BSPDetailModal from '../components/BSPDetailModal';
import { BOOK_TYPE_OPTIONS, GENRE_OPTIONS, getGenresForBookType, getThemesForGenre } from '../constants/genreThemes';
import { 
  BookOpen, ArrowLeft, Save, Upload, Image, FileText, CheckCircle, Clock, 
  AlertCircle, Archive, AlertTriangle, Play, Eye, Loader2, Volume2, Edit, 
  RefreshCw, Calendar, Users, MessageSquare, Coins, DollarSign, FileAudio,
  Music, Copy, Link as LinkIcon, ExternalLink, RefreshCcw
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useNotification } from '@/contexts/NotificationContext';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const AuthorBookDetailsPage = () => {
  const { manuscriptId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const coverInputRef = useRef(null);
  const pollingRef = useRef(null);
  
  const { notifyError, notifySuccess } = useNotification();
  const [manuscript, setManuscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  
  // TTS modals
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [showTTSProgress, setShowTTSProgress] = useState(false);
  const [ttsProgress, setTtsProgress] = useState({ completed: 0, total: 0, isComplete: false });
  
  // Activation state
  const [isActivating, setIsActivating] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationAction, setActivationAction] = useState(null);
  const [activationSteps, setActivationSteps] = useState([]);
  const [activationComplete, setActivationComplete] = useState(false);
  
  // Outline modal - supports deep link via ?outline=1
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  
  // Post-Ingestion Outline Editor modal
  const [showPostEditModal, setShowPostEditModal] = useState(false);
  
  // BSP Report modal
  const [showBSPModal, setShowBSPModal] = useState(false);

  // Cover modal
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [coverOptStatus, setCoverOptStatus] = useState(null);

  // SEO Index state
  const [indexingInFlight, setIndexingInFlight] = useState(false);
  const [indexError, setIndexError] = useState(null);
  
  // Sample Selection modal
  const [showSampleModal, setShowSampleModal] = useState(false);
  
  // Sample Stats state
  const [sampleStats, setSampleStats] = useState(null);
  const [loadingSampleStats, setLoadingSampleStats] = useState(false);
  
  // Preview Share URL state - uses public sample route for social media sharing
  const [previewShareUrl, setPreviewShareUrl] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Generate the shareable URL when manuscript data is loaded
  useEffect(() => {
    if (manuscript?.id && manuscript?.hasSample) {
      setPreviewShareUrl(`${window.location.origin}/sample/${manuscript.id}`);
    }
  }, [manuscript?.id, manuscript?.hasSample]);
  
  // Book History state
  const [bookHistory, setBookHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    isbn: '',
    book_type: '',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchManuscript();
    fetchBookHistory();
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [manuscriptId]);

  // Deep link support: open outline modal via ?outline=1
  useEffect(() => {
    if (searchParams.get('outline') === '1') {
      setShowOutlineModal(true);
    }
  }, [searchParams]);

  // Handle outline modal open/close with URL sync
  const handleOpenOutline = () => {
    setSearchParams({ outline: '1' });
    setShowOutlineModal(true);
  };

  const handleCloseOutline = () => {
    searchParams.delete('outline');
    setSearchParams(searchParams);
    setShowOutlineModal(false);
  };

  const handleOutlineComplete = () => {
    // Refresh manuscript data after outline edits
    fetchManuscript();
  };

  // Post-Ingestion Outline Editor handlers
  const handlePostEditComplete = (result) => {
    console.log('[AuthorBookDetailsPage] Post-edit complete:', result);
    // Refresh manuscript data to show updated status
    fetchManuscript();
    if (result?.deactivated) {
      notifySuccess('Manuscript updated! Please re-activate to make changes live.');
    }
  };

  // P0 FINAL HOLE: TTS polling REMOVED - Status comes from page refresh only
  // useEffect for startPolling DELETED - no polling allowed

  const fetchManuscript = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/manuscripts/${manuscriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setManuscript(data);
      setFormData({
        title: data.title || '',
        subtitle: data.subtitle || '',
        description: data.description || data.summary || '',
        isbn: data.isbn || '',
        book_type: data.book_type || 'non-fiction',
        genre: data.genre || '',
        theme: data.theme || '',
        purchase_url: data.purchase_url || '',
        is_promotional: data.is_promotional || false
      });
      
      // Fetch sample stats if this book has a sample
      if (data.hasSample) {
        fetchSampleStats();
      }
    } catch (error) {
      console.error('Error fetching manuscript:', error);
      notifyError('Failed to load book');
      navigate('/author-console');
    } finally {
      setLoading(false);
    }
  };

  // P0 FINAL HOLE: TTS polling REMOVED - Status comes from tts_jobs via page refresh only
  // fetchManuscriptStatus DELETED - No polling allowed

  const fetchBookHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/manuscripts/${manuscriptId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookHistory(response.data);
    } catch (error) {
      console.error('Error fetching book history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch sample play stats for this manuscript
  const fetchSampleStats = async () => {
    setLoadingSampleStats(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/admin/analytics/samples/manuscript/${manuscriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSampleStats(response.data);
    } catch (error) {
      // Not an error if 404 - just means no stats yet or no sample
      if (error.response?.status !== 404) {
        console.error('Error fetching sample stats:', error);
      }
      setSampleStats(null);
    } finally {
      setLoadingSampleStats(false);
    }
  };

  const handleIndexManuscript = async () => {
    setIndexingInFlight(true);
    setIndexError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.post(
        `${API}/manuscripts/${manuscriptId}/index`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.seo_slug) {
        setManuscript(prev => ({ ...prev, seo_indexed: true, seo_slug: res.data.seo_slug }));
        notifySuccess('Manuscript indexed successfully');
      }
    } catch (err) {
      setIndexError(err.response?.data?.detail || 'Indexing failed — please try again');
    } finally {
      setIndexingInFlight(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API}/manuscripts/${manuscriptId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[SUCCESS] ' + 'Changes saved');
      await fetchManuscript();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCoverSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      notifyError('Invalid file type. Please upload PNG, JPG, GIF, or WebP.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      notifyError('Cover image must be under 10MB');
      return;
    }
    
    setCoverOptStatus('uploading');
    setShowCoverModal(false);
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('cover', file);
      
      const token = localStorage.getItem('accessToken');
      const res = await axios.post(`${API}/covers/${manuscriptId}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.cover_image_url) {
        setCoverOptStatus('done');
        notifySuccess('Cover uploaded and optimized');
        await fetchManuscript();
      }
    } catch (error) {
      console.error('Error uploading cover:', error);
      setCoverOptStatus('error');
      notifyError(error.response?.data?.detail || 'Failed to upload cover');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleOptimizeExistingCover = async () => {
    setCoverOptStatus('optimizing');
    setShowCoverModal(false);
    setUploadingCover(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.post(`${API}/covers/${manuscriptId}/optimize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCoverOptStatus('done');
      if (res.data?.optimized) {
        notifySuccess('Cover optimized successfully');
      } else {
        notifySuccess(res.data?.message || 'Cover already optimized');
      }
      await fetchManuscript();
    } catch (error) {
      console.error('Error optimizing cover:', error);
      setCoverOptStatus('error');
      notifyError(error.response?.data?.detail || 'Failed to optimize cover');
    } finally {
      setUploadingCover(false);
    }
  };

  // P0 FINAL HOLE: TTS is backend-owned. No user-initiated generation allowed.
  // handleStartTTS REMOVED - System auto-triggers TTS after ingestion

  const handleActivation = async () => {
    setIsActivating(true);
    setActivationComplete(false);
    
    // Initialize progress steps for activation
    if (activationAction === 'activate') {
      setActivationSteps([
        { id: 'activate', label: 'Activating book...', status: 'pending' },
        { id: 'summary', label: 'Generating AI summary...', status: 'pending' },
        { id: 'bsp', label: 'Creating book profile...', status: 'pending' },
        { id: 'ei', label: 'Generating Ephemeral Intelligence for Ask Audia...', status: 'pending' }
      ]);
    }
    
    try {
      const token = localStorage.getItem('accessToken');

      if (activationAction === 'activate') {
        // Step 1: Start activation
        setActivationSteps(prev => prev.map(s => 
          s.id === 'activate' ? { ...s, status: 'running' } : s
        ));
        
        // Use Publisher-parity endpoint: reactivate-with-reprocess
        // This generates AI summary + BSP + EI + activates the book
        const endpoint = `${API}/manuscripts/${manuscriptId}/reactivate-with-reprocess`;
        
        console.log('[AuthorBookDetailsPage] Calling activation endpoint:', endpoint);
        
        // Update to show summary step starting
        setActivationSteps(prev => prev.map(s => 
          s.id === 'activate' ? { ...s, status: 'complete' } :
          s.id === 'summary' ? { ...s, status: 'running' } : s
        ));
        
        const response = await axios.post(
          endpoint,
          {
            regenerate_summary: true,
            reprocess_bsp: true,
            reprocess_tts: false
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        console.log('[AuthorBookDetailsPage] Activation POST succeeded:', { 
          activationAction, 
          manuscriptId,
          response: response.data 
        });
        
        // Parse response to update steps
        const { processes_triggered = [], ei = {}, bsp_status } = response.data;
        
        // Update summary step
        const hasSummary = processes_triggered.includes('summary_generation');
        setActivationSteps(prev => prev.map(s => 
          s.id === 'summary' ? { ...s, status: 'complete', label: hasSummary ? 'AI summary generated' : 'AI summary ready' } : s
        ));
        
        // Update BSP step - now shows real status from backend
        await new Promise(resolve => setTimeout(resolve, 200));
        const hasBsp = processes_triggered.includes('bsp_generation');
        setActivationSteps(prev => prev.map(s => {
          if (s.id === 'bsp') {
            if (bsp_status === 'success') {
              return { ...s, status: 'complete', label: 'Book profile created successfully' };
            } else if (bsp_status === 'rejected') {
              return { ...s, status: 'warning', label: 'Book profile created (quality review needed)' };
            } else if (bsp_status === 'queued' || hasBsp) {
              return { ...s, status: 'complete', label: 'Book profile generation queued' };
            } else {
              return { ...s, status: 'complete', label: 'Book profile ready' };
            }
          }
          return s;
        }));
        
        // Update EI step based on response
        await new Promise(resolve => setTimeout(resolve, 200));
        const hasEi = processes_triggered.includes('ephemeral_intelligence');
        const eiStatus = ei?.status || 'skipped';
        
        setActivationSteps(prev => prev.map(s => {
          if (s.id === 'ei') {
            if (eiStatus === 'complete' || hasEi) {
              return { ...s, status: 'complete', label: 'Ephemeral Intelligence generated for Ask Audia' };
            } else if (eiStatus === 'exists') {
              return { ...s, status: 'complete', label: 'Ephemeral Intelligence ready' };
            } else if (eiStatus === 'error') {
              return { ...s, status: 'warning', label: `EI: ${ei?.reason || 'Generation failed'}` };
            } else {
              return { ...s, status: 'complete', label: 'Ephemeral Intelligence skipped' };
            }
          }
          return s;
        }));
        
        // Mark activation as complete
        await new Promise(resolve => setTimeout(resolve, 300));
        setActivationComplete(true);
        
        // Auto-index on first activation
        if (!manuscript?.seo_indexed) {
          try {
            const indexRes = await axios.post(
              `${API}/manuscripts/${manuscriptId}/index`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (indexRes.data?.seo_slug) {
              // State will refresh via fetchManuscript below
            }
          } catch {
            console.warn('Activated — auto-indexing failed, user can retry manually');
          }
        }
        
        // Refresh manuscript data after short delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        await fetchManuscript();
        
      } else {
        // Deactivation - leave as-is (minimal scope)
        const endpoint = `${API}/manuscripts/${manuscriptId}/deactivate`;
        
        console.log('[AuthorBookDetailsPage] Calling deactivation endpoint:', endpoint);
        
        await axios.post(endpoint, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('[AuthorBookDetailsPage] Deactivation POST succeeded:', { 
          activationAction, 
          manuscriptId 
        });
        
        setShowActivationModal(false);
        await fetchManuscript();
      }
      
    } catch (error) {
      console.error('[AuthorBookDetailsPage] Activation failed:', error?.response?.data || error);
      notifyError('Failed to change activation status');
      setActivationSteps(prev => prev.map(s => 
        s.status === 'running' ? { ...s, status: 'error' } : s
      ));
    } finally {
      setIsActivating(false);
    }
  };

  const handlePreview = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API}/manuscripts/${manuscriptId}/open`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      navigate(`/player/${response.data.session_id}`, { 
        state: { returnTo: `/author/titles/${manuscriptId}` } 
      });
    } catch (error) {
      // Log detailed error for debugging
      const errorData = error.response?.data;
      const traceId = errorData?.detail?.trace_id || errorData?.error?.trace_id;
      const errorMsg = errorData?.detail?.message || errorData?.detail || errorData?.error?.message || error.message;
      console.error(`[PREVIEW] /open failed: status=${error.response?.status} trace_id=${traceId} error=${errorMsg}`, errorData);
      notifyError(errorMsg || 'Failed to open preview');
    }
  };

  // Preview the book sample - NO credits debited, NO analytics tracked
  const handlePreviewSample = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API}/manuscripts/${manuscriptId}/sample/preview`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      navigate(`/player/${response.data.session_id}`, { 
        state: { 
          returnTo: `/author/titles/${manuscriptId}`,
          samplePreview: true  // Flag for player to load sample segments only
        } 
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      console.error('[SAMPLE_PREVIEW] Failed:', errorMsg);
      notifyError(errorMsg || 'Failed to preview sample');
    }
  };
  
  // Copy preview URL to clipboard
  const copyPreviewUrl = async () => {
    if (!previewShareUrl) return;
    
    try {
      await navigator.clipboard.writeText(previewShareUrl);
      setCopySuccess(true);
      notifySuccess('Link copied to clipboard!');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('[COPY] Failed:', error);
      notifyError('Failed to copy link');
    }
  };

  const getCoverUrl = () => {
    if (manuscript?.cover_image_url) {
      return `${BACKEND_URL}${manuscript.cover_image_url}?t=${Date.now()}`;
    }
    if (manuscript?.simple_ingest_job_id) {
      return `${BACKEND_URL}/api/simple-ingest/jobs/${manuscript.simple_ingest_job_id}/cover?t=${Date.now()}`;
    }
    return null;
  };

  const getProcessingStatus = () => {
    if (!manuscript) return null;
    
    const audioStatus = manuscript.audio_status;
    
    // Simple ingest uses 'ready' status when audio is complete
    if (audioStatus === 'completed' || audioStatus === 'partial' || audioStatus === 'ready') {
      return { status: 'completed', label: 'Audio Generation Complete', color: 'emerald' };
    }
    if (audioStatus === 'processing') {
      return { status: 'processing', label: 'Generating Audio...', color: 'amber' };
    }
    if (audioStatus === 'failed') {
      return { status: 'failed', label: 'Audio Failed', color: 'red' };
    }
    if (manuscript.status === 'processed' || manuscript.ingestion_version === 3) {
      return { status: 'ready', label: 'Ready for Audio', color: 'blue' };
    }
    return { status: 'pending', label: 'Pending', color: 'slate' };
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: '2-digit', day: '2-digit', year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <CreatorNav />
        <CreatorTopMenuBar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }

  const coverUrl = getCoverUrl();
  const processingStatus = getProcessingStatus();
  const availableGenres = formData.book_type ? getGenresForBookType(formData.book_type) : GENRE_OPTIONS;
  const availableThemes = formData.genre ? getThemesForGenre(formData.genre) : [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <CreatorNav />
      <CreatorTopMenuBar />
      
      <main className="mx-auto max-w-5xl px-4 py-8 pb-24 md:pb-8">
        {/* Back button */}
        <Link
          to="/author-console"
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Author Console
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">{manuscript?.title}</h1>
            <p className="text-slate-400 mt-1">
              By <strong className="text-slate-200">{manuscript?.author}</strong>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Cover & Status */}
          <div className="space-y-6">
            {/* Cover */}
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
              <h2 className="text-lg font-semibold mb-4">Cover Image</h2>
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleCoverSelect}
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
              />
              <div
                onClick={() => setShowCoverModal(true)}
                className="aspect-[3/4] rounded-xl border-2 border-dashed border-slate-600 bg-slate-800 flex items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-slate-700 transition-colors overflow-hidden relative"
              >
                {coverUrl ? (
                  <>
                    <img src={coverUrl} alt="Cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Upload className="w-8 h-8 text-white" />
                    </div>
                  </>
                ) : (
                  <div className="text-center p-4">
                    <Image className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Click to manage cover</p>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF, WebP (max 10MB)</p>
                  </div>
                )}
                {uploadingCover && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center flex-col gap-2">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                    <p className="text-xs text-white">
                      {coverOptStatus === 'uploading' ? 'Optimizing image for NewAudia...' : 
                       coverOptStatus === 'optimizing' ? 'Optimizing existing cover...' : 'Processing...'}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Action Buttons under cover */}
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreview}
                  disabled={!manuscript?.audio_status || manuscript.audio_status === 'pending'}
                  className="w-full text-xs"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Preview
                </Button>
              </div>
              
              {/* Post-Ingestion Outline Editor Button - Only show for completed manuscripts */}
              {(processingStatus?.status === 'completed' || manuscript?.status === 'active' || manuscript?.status === 'deactivated') && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    onClick={() => setShowPostEditModal(true)}
                    className="w-full text-xs bg-yellow-400 text-black hover:bg-yellow-500 hover:text-black"
                    data-testid="post-edit-outline-btn"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Outline Editor
                  </Button>
                  <p className="text-[10px] text-slate-500 mt-1 text-center">
                    Edit text & reorder after ingestion
                  </p>
                </div>
              )}
              
              {/* Create/Edit Sample Button - Only show when audio is completed */}
              {processingStatus?.status === 'completed' && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSampleModal(true)}
                      className={`flex-1 text-xs ${
                        manuscript?.hasSample 
                          ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10' 
                          : 'bg-sky-400 text-black border-sky-400 hover:bg-sky-300 hover:border-sky-300'
                      }`}
                      data-testid="create-sample-btn"
                    >
                      <Music className="w-3 h-3 mr-1" />
                      {manuscript?.hasSample ? 'Edit Sample' : 'Create a Sample'}
                    </Button>
                    {/* Preview Sample Button - Only visible when sample exists */}
                    {manuscript?.hasSample && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviewSample}
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 px-2"
                        data-testid="preview-sample-btn"
                        title="Preview your sample"
                      >
                        <Play className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {/* Sample Stats - Show when sample exists */}
                  {manuscript?.hasSample && sampleStats && (
                    <div className="mt-3 p-3 bg-teal-900/20 border border-teal-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Music className="w-3 h-3 text-teal-400" />
                        <span className="text-xs font-medium text-teal-300">Sample Play Stats</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-slate-500">Total Plays</p>
                          <p className="text-white font-semibold">{sampleStats.total_plays || 0}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Last 7 Days</p>
                          <p className="text-cyan-400 font-semibold">{sampleStats.plays_last_7_days || 0}</p>
                        </div>
                      </div>
                      <p className="text-[9px] text-slate-500 mt-2 italic">
                        Sample plays help attract new listeners but do not generate reading credits.
                      </p>
                    </div>
                  )}
                  
                  {/* Share Preview URL Section */}
                  {manuscript?.hasSample && previewShareUrl && (
                    <div className="mt-3 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <LinkIcon className="w-3 h-3 text-purple-400" />
                        <span className="text-xs font-medium text-purple-300">Share Sample Link</span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={previewShareUrl}
                            className="flex-1 bg-slate-800/50 border border-slate-600/50 rounded px-2 py-1.5 text-xs text-slate-300 truncate"
                            data-testid="preview-share-url"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={copyPreviewUrl}
                            className={`px-2 ${copySuccess ? 'text-green-400' : 'text-purple-400 hover:text-purple-300'}`}
                            data-testid="copy-preview-url-btn"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <a
                            href={previewShareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-400 hover:text-purple-300 p-1"
                            title="Open in new tab"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                        <p className="text-[9px] text-slate-500 italic">
                          Share this link on social media to let potential readers preview your sample.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Activate/Deactivate */}
              {processingStatus?.status === 'completed' && (
                <div className="mt-3">
                  {manuscript?.is_active ? (
                    <Button
                      className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white"
                      disabled={isActivating}
                      onClick={() => {
                        setActivationAction('deactivate');
                        setShowActivationModal(true);
                      }}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
                      disabled={isActivating}
                      onClick={() => {
                        setActivationAction('activate');
                        setShowActivationModal(true);
                      }}
                    >
                      Activate
                    </Button>
                  )}
                </div>
              )}

              {/* SEO Index */}
              {manuscript?.is_active === true && (
                manuscript?.seo_indexed ? (
                  <div className="mt-3 w-full text-center space-y-1" data-testid="seo-indexed-label">
                    <p className="text-xs font-semibold text-emerald-400">&#10003; Manuscript Indexed</p>
                    <button
                      className="text-xs text-teal-400 hover:text-teal-300 underline underline-offset-2 transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://www.newaudia.com/book/${manuscript.seo_slug}`);
                        notifySuccess('Public URL copied to clipboard');
                      }}
                      data-testid="seo-copy-url-btn"
                    >
                      newaudia.com/book/{manuscript.seo_slug}
                    </button>
                  </div>
                ) : (
                  <div className="mt-3" data-testid="seo-index-container">
                    <Button
                      className="w-full bg-[#00A896] hover:bg-[#009080] text-white"
                      disabled={indexingInFlight}
                      onClick={handleIndexManuscript}
                      data-testid="seo-index-btn"
                    >
                      {indexingInFlight ? 'Indexing…' : 'Index for Search'}
                    </Button>
                    {indexError && (
                      <p className="text-xs text-red-400 mt-1 text-center">{indexError}</p>
                    )}
                  </div>
                )
              )}
            </section>

            {/* Processing Status */}
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
              <h2 className="text-lg font-semibold mb-4">Audio Status</h2>
              
              {processingStatus?.status === 'completed' ? (
                <div className="text-center p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
                  <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-emerald-300 font-medium">Audio Complete</p>
                  <p className="text-xs text-slate-400 mt-1">{manuscript?.segment_count || manuscript?.chapter_count} segments</p>
                </div>
              ) : processingStatus?.status === 'processing' ? (
                <div className="text-center p-4 bg-amber-900/20 border border-amber-500/30 rounded-xl">
                  <Loader2 className="w-8 h-8 text-amber-400 mx-auto mb-2 animate-spin" />
                  <p className="text-sm text-amber-300 font-medium">Processing Audio...</p>
                  <p className="text-xs text-slate-400 mt-1">System is working in background</p>
                </div>
              ) : processingStatus?.status === 'failed' ? (
                <div className="text-center p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
                  <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-red-300 font-medium">Audio Generation Failed</p>
                  <p className="text-xs text-slate-400 mt-2">Contact support for assistance</p>
                </div>
              ) : (
                <div className="text-center p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                  <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-300 font-medium">Pending</p>
                </div>
              )}
            </section>

            {/* Book Stats */}
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
              <h2 className="text-lg font-semibold mb-4">Book Stats</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
                  <span className="text-sm text-slate-400">Words</span>
                  <span className="font-semibold">{formatNumber(manuscript?.total_words || manuscript?.word_count)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
                  <span className="text-sm text-slate-400">Chapters</span>
                  <span className="font-semibold">{manuscript?.chapter_count || manuscript?.segment_count || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
                  <span className="text-sm text-slate-400">Segments</span>
                  <span className="font-semibold">{manuscript?.segment_count || manuscript?.chapter_count || 0}</span>
                </div>
                {/* <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-slate-400">Processing Cost</span>
                  <span className="font-semibold text-amber-400">${(manuscript?.api_cost_total || 0).toFixed(4)}</span>
                </div> */}
              </div>
            </section>
          </div>

          {/* Right Column - Title Info & History */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Information */}
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
              <h2 className="text-lg font-semibold mb-4">Title Information</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Title Name *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Subtitle</Label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">ISBN</Label>
                  <Input
                    value={formData.isbn}
                    onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Book Type</Label>
                    <select
                      value={formData.book_type}
                      onChange={(e) => setFormData({ ...formData, book_type: e.target.value, genre: '', theme: '' })}
                      className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 text-white py-2 px-3"
                    >
                      <option value="">Select Type</option>
                      {BOOK_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Genre</Label>
                    <select
                      value={formData.genre}
                      onChange={(e) => setFormData({ ...formData, genre: e.target.value, theme: '' })}
                      className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 text-white py-2 px-3"
                    >
                      <option value="">Select Genre</option>
                      {availableGenres.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Theme</Label>
                  <select
                    value={formData.theme}
                    onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                    className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 text-white py-2 px-3"
                  >
                    <option value="">Select Theme</option>
                    {availableThemes.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="text-slate-300">Purchase URL</Label>
                  <Input
                    value={formData.purchase_url}
                    onChange={(e) => setFormData({ ...formData, purchase_url: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Description (About the Book)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-600 min-h-[120px]"
                    placeholder="Enter a description of your book..."
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="promotional"
                    checked={formData.is_promotional}
                    onChange={(e) => setFormData({ ...formData, is_promotional: e.target.checked })}
                    className="rounded bg-slate-800 border-slate-600"
                  />
                  <Label htmlFor="promotional" className="text-slate-300 cursor-pointer">
                    Promotional (Free)
                  </Label>
                </div>
                
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-indigo-600 hover:bg-indigo-500"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </section>

            {/* Book History */}
            <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-5">
              <h2 className="text-lg font-semibold mb-4">Book History</h2>
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Calendar className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Upload Date</p>
                    <p className="font-semibold text-sm">{formatDate(manuscript?.created_at)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Clock className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Last Accessed</p>
                    <p className="font-semibold text-sm">{formatDate(bookHistory?.last_accessed || manuscript?.updated_at)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Calendar className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Days Available</p>
                    <p className="font-semibold text-sm">{bookHistory?.days_available || 0}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Users className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Unique Readers</p>
                    <p className="font-semibold text-sm">{formatNumber(bookHistory?.unique_readers || 0)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Users className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Guest Readers</p>
                    <p className="font-semibold text-sm">{formatNumber(bookHistory?.guest_readers || 0)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Users className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Avg. Readers/Day</p>
                    <p className="font-semibold text-sm">{(bookHistory?.avg_readers_per_day || 0).toFixed(1)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <BookOpen className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Total Words Read</p>
                    <p className="font-semibold text-sm">{formatNumber(bookHistory?.total_words_read || manuscript?.total_words_consumed || 0)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Clock className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Engagement (mins)</p>
                    <p className="font-semibold text-sm">{(bookHistory?.total_engagement_minutes || 0).toFixed(1)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <MessageSquare className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Audia Questions</p>
                    <p className="font-semibold text-sm">{formatNumber(bookHistory?.total_audia_questions || 0)}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <Coins className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Reader Credits</p>
                    <p className="font-semibold text-sm text-amber-400">{(bookHistory?.total_reader_credits || 0).toFixed(2)}</p>
                  </div>
                </div>
              )}
            </section>

            {/* Book Semantic Profile (BSP) Section */}
            <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mt-6">
              <h2 className="text-lg font-semibold mb-4">Book Semantic Profile (BSP)</h2>
              <p className="text-sm text-slate-400 mb-4">
                View the AI-generated semantic analysis of your book, including themes, characters, and reader insights.
              </p>
              <Button
                onClick={() => setShowBSPModal(true)}
                variant="outline"
                className="w-full sm:w-auto border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                data-testid="view-bsp-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                View BSP Report
              </Button>
            </section>
          </div>
        </div>
      </main>

      <CreatorMobileBottomNav />
      <UniversalFooter />

      {/* P0 FINAL HOLE: TTSDecisionModal REMOVED - TTS is system-owned */}
      {/* No user action for TTS generation is allowed */}

      {/* P0 DIRECTIVE: TTSProgressModal is FORBIDDEN and has been REMOVED */}
      {/* TTS is auto-triggered and monitored by backend only */}

      {/* Outline Editor Modal - Opens over title page, not a separate route */}
      <OutlineEditorModal
        isOpen={showOutlineModal}
        onClose={handleCloseOutline}
        manuscriptId={manuscriptId}
        onComplete={handleOutlineComplete}
      />

      {/* Post-Ingestion Outline Editor Modal - For editing after TTS completion */}
      <PostIngestionOutlineEditor
        isOpen={showPostEditModal}
        onClose={() => setShowPostEditModal(false)}
        manuscriptId={manuscriptId}
        onSaveComplete={handlePostEditComplete}
      />

      {/* Activation Modal */}
      <Dialog open={showActivationModal} onOpenChange={(open) => {
        // Only allow closing if not activating or if activation is complete
        if (!isActivating || activationComplete) {
          setShowActivationModal(open);
          if (!open) {
            setActivationSteps([]);
            setActivationComplete(false);
          }
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-md">
          <DialogHeader>
            <DialogTitle>
              {activationComplete 
                ? 'Activation Complete' 
                : (activationAction === 'activate' ? 'Activate Book' : 'Deactivate Book')
              }
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {activationComplete 
                ? 'Your book is now live in the NewAudia library and ready for readers.'
                : (activationAction === 'activate' 
                  ? 'This will make your book available to readers in the NewAudia library.'
                  : 'This will remove your book from the NewAudia library. Readers will no longer be able to access it.'
                )
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Progress Steps - Show during activation */}
          {activationAction === 'activate' && activationSteps.length > 0 && (
            <div className="space-y-3 py-4 border-t border-slate-700">
              {activationSteps.map((step) => (
                <div key={step.id} className="flex items-center gap-3">
                  {step.status === 'pending' && <Clock className="w-4 h-4 text-slate-500" />}
                  {step.status === 'running' && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
                  {step.status === 'complete' && <CheckCircle className="w-4 h-4 text-green-400" />}
                  {step.status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                  {step.status === 'error' && <AlertCircle className="w-4 h-4 text-red-400" />}
                  <span className={`text-sm ${
                    step.status === 'running' ? 'text-amber-300' : 
                    step.status === 'complete' ? 'text-green-300' : 
                    step.status === 'warning' ? 'text-amber-300' :
                    step.status === 'error' ? 'text-red-300' :
                    'text-slate-400'
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* Completion Message */}
          {activationComplete && (
            <div className="py-4 border-t border-slate-700">
              <div className="flex items-center gap-3 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-green-300 font-medium">Book Activated Successfully</p>
                  <p className="text-slate-400 text-sm">Your book is now available to readers</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {activationComplete ? (
              <Button 
                onClick={() => {
                  setShowActivationModal(false);
                  setActivationSteps([]);
                  setActivationComplete(false);
                }}
                className="bg-green-600 hover:bg-green-500"
              >
                Done
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setShowActivationModal(false)}
                  disabled={isActivating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleActivation}
                  disabled={isActivating}
                  className={activationAction === 'activate' 
                    ? 'bg-green-600 hover:bg-green-500' 
                    : 'bg-red-600 hover:bg-red-500'
                  }
                >
                  {isActivating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    activationAction === 'activate' ? 'Activate' : 'Deactivate'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cover Image Options Modal */}
      <Dialog open={showCoverModal} onOpenChange={setShowCoverModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Cover Image</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose an action for the book cover
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              onClick={() => { setShowCoverModal(false); coverInputRef.current?.click(); }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="cover-upload-new-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload New Cover
            </Button>
            {coverUrl && (
              <Button
                onClick={handleOptimizeExistingCover}
                variant="outline"
                className="w-full border-teal-600/50 text-teal-400 hover:bg-teal-600/10"
                data-testid="cover-optimize-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Optimize Existing Cover
              </Button>
            )}
            <Button
              onClick={() => setShowCoverModal(false)}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
              data-testid="cover-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BSP Report Modal */}
      <BSPDetailModal
        isOpen={showBSPModal}
        onClose={() => setShowBSPModal(false)}
        manuscriptId={manuscriptId}
        bookTitle={manuscript?.title}
        apiBase="/api/creator/manuscripts"
        showHistory={false}
      />

      {/* Sample Selection Modal */}
      <SampleSelectionModal
        isOpen={showSampleModal}
        onClose={() => setShowSampleModal(false)}
        manuscriptId={manuscriptId}
        hasExistingSample={manuscript?.hasSample || false}
        onSampleCreated={() => fetchManuscript()}
      />
    </div>
  );
};

export default AuthorBookDetailsPage;
